import { inject, Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { LoadingState } from '@tsin-core/entities';
import { CandidateMessage, CommunicationsHttpService } from '@tsin-core/services/http/communications.service';
import { catchError, tap } from 'rxjs';
import { ClearCommunicationThread, LoadCommunicationThread, MarkCommunicationMessagesRead, SendCommunicationMessage } from './communications.actions';

export class CommunicationsStateModel {
  /** Currently loaded thread messages / Messages du fil chargé actuellement */
  messages: CandidateMessage[] = [];
  /** Application ID for the currently open thread / Identifiant de la candidature du fil ouvert */
  openApplicationId: string | null = null;
  /** Whether the panel is sending a message / Envoi d'un message en cours */
  sending: boolean = false;
  /** Loading state for thread fetch / État de chargement pour la récupération du fil */
  loading: LoadingState = LoadingState.notLoading;
  /** Error message from latest operation / Message d'erreur de la dernière opération */
  error: string | null = null;
}

@State<CommunicationsStateModel>({
  name: 'communicationsState',
  defaults: {
    messages: [],
    openApplicationId: null,
    sending: false,
    loading: LoadingState.notLoading,
    error: null,
  },
})
@Injectable()
export class CommunicationsState {
  private readonly svc = inject(CommunicationsHttpService);

  // ─── Selectors ───────────────────────────────────────────────────────────

  @Selector()
  static messages(s: CommunicationsStateModel) {
    return s.messages;
  }

  @Selector()
  static sending(s: CommunicationsStateModel) {
    return s.sending;
  }

  @Selector()
  static loading(s: CommunicationsStateModel) {
    return s.loading;
  }

  @Selector()
  static error(s: CommunicationsStateModel) {
    return s.error;
  }

  @Selector()
  static unreadCount(s: CommunicationsStateModel) {
    return s.messages.filter((m) => m.direction === 'inbound' && !m.readAt).length;
  }

  // ─── Actions ─────────────────────────────────────────────────────────────

  @Action(LoadCommunicationThread)
  loadThread(ctx: StateContext<CommunicationsStateModel>, { applicationId }: LoadCommunicationThread) {
    ctx.patchState({ loading: LoadingState.loadingList, error: null, openApplicationId: applicationId });
    return this.svc.getThread(applicationId).pipe(
      tap((messages) => ctx.patchState({ messages, loading: LoadingState.notLoading })),
      catchError((err) => {
        ctx.patchState({ loading: LoadingState.notLoading, error: err?.message ?? 'Load failed / Chargement échoué' });
        return [];
      }),
    );
  }

  @Action(SendCommunicationMessage)
  sendMessage(ctx: StateContext<CommunicationsStateModel>, { payload }: SendCommunicationMessage) {
    ctx.patchState({ sending: true, error: null });
    return this.svc.sendMessage(payload).pipe(
      tap((msg) => {
        const state = ctx.getState();
        ctx.patchState({ messages: [...state.messages, msg], sending: false });
      }),
      catchError((err) => {
        ctx.patchState({ sending: false, error: err?.message ?? 'Send failed / Envoi échoué' });
        return [];
      }),
    );
  }

  @Action(MarkCommunicationMessagesRead)
  markRead(ctx: StateContext<CommunicationsStateModel>, { messageIds }: MarkCommunicationMessagesRead) {
    const now = new Date().toISOString();
    const updated = ctx.getState().messages.map((m) => (messageIds.includes(m._id) ? { ...m, readAt: now } : m));
    ctx.patchState({ messages: updated });
    return this.svc.markRead(messageIds);
  }

  @Action(ClearCommunicationThread)
  clearThread(ctx: StateContext<CommunicationsStateModel>) {
    ctx.patchState({
      messages: [],
      openApplicationId: null,
      sending: false,
      loading: LoadingState.notLoading,
      error: null,
    });
  }
}
